import { useRouter } from "next/router"
import { type PropsWithChildren, type RefObject, useRef, useState } from "react"
import { DidomiSDK } from "@didomi/react"
import { GoogleTagManager } from "@next/third-parties/google"
import { useHeaderHeight } from "@octopus-energy/coral/hooks"
import { CoralConfigProvider } from "@octopus-energy/coral/providers"
import { IconsThemeProvider } from "@octopus-energy/icons"
import { env } from "@oefr/env/client"
import { IntlErrorCode, NextIntlClientProvider } from "next-intl"
import { NuqsAdapter } from "nuqs/adapters/next/pages"
import { usePartnerFromDomain } from "@/features/partners"
import { getPartnerConfig } from "@/features/partners/utils/getPartnerConfig"
import { config as coralConfig, coralIconConfig } from "@/lib/coral/theme"
import DatadogInit from "@/lib/datadog/Datadog-init"
import { CustomGoogleTagManager } from "@/lib/google"
import { CustomApiProvider } from "./ApiProvider"
import { CustomAuthProvider } from "./AuthProvider"
import { PersistentQueryParamsProvider } from "./PersistentQueryParamsProvider"

type Props = PropsWithChildren<{ messages?: IntlMessages }>

export function Providers({ children, messages }: Props) {
    const router = useRouter()
    const headerRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement> // TODO: remove type assertion when Coral types support react v19
    const [isDidomiReady, setIsDidomiReady] = useState(false)
    const partner = usePartnerFromDomain()

    return (
        <CustomAuthProvider>
            <CustomApiProvider>
                <NextIntlClientProvider
                    locale={router.locale}
                    timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
                    messages={messages}
                    onError={(error) => {
                        if (error.code === IntlErrorCode.MISSING_MESSAGE) {
                            // this is caught by the getMessageFallback function
                        } else {
                            console.error(error.message)
                        }
                    }}
                    getMessageFallback={({ error, key, namespace }) => {
                        const path = [namespace, key].filter((part) => part != null).join(".")

                        if (error.code === IntlErrorCode.MISSING_MESSAGE) {
                            console.error(`Missing message: ${path} at route: ${router.pathname}`)
                        }

                        return path
                    }}
                >
                    <PersistentQueryParamsProvider>
                        <NuqsAdapter>
                            {env.NEXT_PUBLIC_VERCEL_ENV !== "development" && (
                                <>
                                    <DidomiSDK
                                        apiKey={env.NEXT_PUBLIC_DIDOMI_API_KEY}
                                        iabVersion={2}
                                        gdprAppliesGlobally
                                        onReady={() => setIsDidomiReady(true)}
                                    />
                                    {isDidomiReady && (
                                        <>
                                            <CustomGoogleTagManager
                                                gtmId={getPartnerConfig("default").GTMId}
                                            />
                                            {partner !== "default" && (
                                                <GoogleTagManager
                                                    gtmId={getPartnerConfig(partner).GTMId}
                                                />
                                            )}
                                            <DatadogInit />
                                        </>
                                    )}
                                </>
                            )}
                            <CoralConfigProvider
                                config={{
                                    headerHeight: useHeaderHeight(headerRef),
                                    headerRef,
                                    ...coralConfig,
                                }}
                            >
                                <IconsThemeProvider defaultProps={coralIconConfig}>
                                    {children}
                                </IconsThemeProvider>
                            </CoralConfigProvider>
                        </NuqsAdapter>
                    </PersistentQueryParamsProvider>
                </NextIntlClientProvider>
            </CustomApiProvider>
        </CustomAuthProvider>
    )
}
